import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import PageInfo from './components/PageInfo';

interface HazardResult {
    hazard: string;
    engine: string;
    risk_level: string;
    expected_annual_damage?: number;
    impact_value?: number;
    detail?: string;
    error?: string;
    loss_distribution?: {
        ead?: number;
        var_95?: number;
        var_99?: number;
    };
}

interface FinancialSummary {
    asset_value: number;
    total_ead: number;
    total_ead_fraction?: number;
    climate_var_npv?: number;
    climate_var_fraction?: number;
}

interface ClimateRiskAssessment {
    _id: string;
    _address_id: string;
    address: string;
    lat: number;
    lon: number;
    asset_value?: number;
    scenario: string;
    year: number;
    asset_class?: string;
    asset_type?: string;
    results: HazardResult[];
    financial_summary?: FinancialSummary;
    tags?: string[];
    updated_at?: string;
    created_at?: string;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

const RISK_LEVEL_COLORS: Record<string, string> = {
    'Very High': '#d32f2f',
    'High': '#f44336',
    'Medium': '#ff9800',
    'Low': '#4caf50',
    'Negligible': '#8bc34a',
    'No Data': '#9e9e9e'
};

const RISK_LEVEL_ORDER = ['Very High', 'High', 'Medium', 'Low', 'Negligible', 'No Data'];

const SCENARIO_LABELS: Record<string, string> = {
    'ssp126': 'SSP1-2.6 (Net Zero)',
    'ssp245': 'SSP2-4.5 (Middle Road)',
    'ssp585': 'SSP5-8.5 (Hot House)'
};

function getHighestRiskLevel(results: HazardResult[]): string {
    if (!results || results.length === 0) return 'No Data';
    let highest = 'No Data';
    let highestIdx = RISK_LEVEL_ORDER.length;
    for (const r of results) {
        const idx = RISK_LEVEL_ORDER.indexOf(r.risk_level);
        if (idx >= 0 && idx < highestIdx) {
            highestIdx = idx;
            highest = r.risk_level;
        }
    }
    return highest;
}

function formatCurrency(value: number | undefined): string {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-EU', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
    }).format(value);
}

const ClimateRiskView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const limit = 20;

    const [assessments, setAssessments] = useState<ClimateRiskAssessment[]>([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [scenarioFilter, setScenarioFilter] = useState(searchParams.get('scenario') || '');
    const [riskLevelFilter, setRiskLevelFilter] = useState(searchParams.get('riskLevel') || '');

    const fetchAssessments = async (page: number) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('limit', String(limit));
            params.set('page', String(page));
            if (searchQuery) params.set('search', searchQuery);
            if (scenarioFilter) params.set('scenario', scenarioFilter);
            if (riskLevelFilter) params.set('riskLevel', riskLevelFilter);

            const headers: Record<string, string> = { 'x-api-key': apiKey };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await axios.get(`${apiUrl}/climate-risk?${params.toString()}`, { headers });
            setAssessments(response.data.assessments || []);
            setPagination(response.data.pagination || null);
        } catch (error) {
            console.error('Error fetching climate risk assessments:', error);
            setAssessments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authLoaded) {
            const page = parseInt(searchParams.get('page') || '1', 10);
            fetchAssessments(page);
        }
    }, [authLoaded, token, searchParams]);

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', String(newPage));
        setSearchParams(params);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params = new URLSearchParams(searchParams);
        params.set('page', '1');
        if (searchQuery) params.set('search', searchQuery);
        else params.delete('search');
        if (scenarioFilter) params.set('scenario', scenarioFilter);
        else params.delete('scenario');
        if (riskLevelFilter) params.set('riskLevel', riskLevelFilter);
        else params.delete('riskLevel');
        setSearchParams(params);
    };

    if (!authLoaded) {
        return <div className="container"><p>{t('common.loading')}</p></div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <h1>
                    <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: 8 }}>thermostat</span>
                    {t('climateRisk.title')}
                </h1>
                <p className="page-subtitle">{t('climateRisk.subtitle')}</p>
            </div>

            <form className="search-bar" onSubmit={handleSearch}>
                <div className="search-inputs" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder={t('climateRisk.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    <select
                        value={scenarioFilter}
                        onChange={(e) => setScenarioFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">{t('climateRisk.allScenarios')}</option>
                        <option value="ssp126">SSP1-2.6 (Net Zero)</option>
                        <option value="ssp245">SSP2-4.5 (Middle Road)</option>
                        <option value="ssp585">SSP5-8.5 (Hot House)</option>
                    </select>
                    <select
                        value={riskLevelFilter}
                        onChange={(e) => setRiskLevelFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">{t('climateRisk.allRiskLevels')}</option>
                        {RISK_LEVEL_ORDER.filter(r => r !== 'No Data').map(level => (
                            <option key={level} value={level}>{level}</option>
                        ))}
                    </select>
                    <button type="submit" className="btn btn-primary">
                        <span className="material-icons" style={{ fontSize: 18 }}>search</span>
                        {t('common.search')}
                    </button>
                </div>
            </form>

            {pagination && (
                <PageInfo
                    count={assessments.length}
                    total={pagination.totalDocuments}
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    label={t('climateRisk.assessments')}
                />
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading')}</p>
                </div>
            ) : assessments.length === 0 ? (
                <div className="empty-state">
                    <span className="material-icons" style={{ fontSize: 48, color: 'var(--text-secondary)' }}>landscape</span>
                    <p>{t('climateRisk.noResults')}</p>
                </div>
            ) : (
                <div className="climate-risk-grid">
                    {assessments.map((assessment) => {
                        const highestRisk = getHighestRiskLevel(assessment.results);
                        const riskColor = RISK_LEVEL_COLORS[highestRisk] || '#9e9e9e';
                        const hazardCount = assessment.results?.length || 0;
                        const highRiskCount = assessment.results?.filter(r =>
                            r.risk_level === 'High' || r.risk_level === 'Very High'
                        ).length || 0;

                        return (
                            <div
                                key={assessment._id}
                                className="climate-risk-card"
                                onClick={() => navigate(`/climate-risk/${assessment._id}`)}
                            >
                                <div className="climate-risk-card-header">
                                    <div className="risk-badge" style={{ backgroundColor: riskColor }}>
                                        {highestRisk}
                                    </div>
                                    {assessment.tags?.includes('PUBLIC') && (
                                        <span className="tag-badge tag-public">PUBLIC</span>
                                    )}
                                </div>

                                <h3 className="climate-risk-address">{assessment.address}</h3>

                                <div className="climate-risk-meta">
                                    <span className="meta-item">
                                        <span className="material-icons">public</span>
                                        {SCENARIO_LABELS[assessment.scenario] || assessment.scenario}
                                    </span>
                                    <span className="meta-item">
                                        <span className="material-icons">calendar_today</span>
                                        {assessment.year}
                                    </span>
                                    {assessment.asset_type && (
                                        <span className="meta-item">
                                            <span className="material-icons">apartment</span>
                                            {assessment.asset_type}
                                        </span>
                                    )}
                                </div>

                                <div className="climate-risk-stats">
                                    <div className="stat">
                                        <span className="stat-label">{t('climateRisk.hazards')}</span>
                                        <span className="stat-value">{hazardCount}</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">{t('climateRisk.highRisk')}</span>
                                        <span className="stat-value" style={{ color: highRiskCount > 0 ? '#f44336' : 'inherit' }}>
                                            {highRiskCount}
                                        </span>
                                    </div>
                                    {assessment.financial_summary && (
                                        <>
                                            <div className="stat">
                                                <span className="stat-label">{t('climateRisk.ead')}</span>
                                                <span className="stat-value">
                                                    {formatCurrency(assessment.financial_summary.total_ead)}
                                                </span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">{t('climateRisk.climateVaR')}</span>
                                                <span className="stat-value">
                                                    {formatCurrency(assessment.financial_summary.climate_var_npv)}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {assessment.updated_at && (
                                    <div className="climate-risk-date">
                                        {t('climateRisk.lastUpdated')}: {new Date(assessment.updated_at).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {pagination && pagination.totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="btn btn-secondary"
                        disabled={!pagination.hasPreviousPage}
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                    >
                        {t('common.previous')}
                    </button>
                    <span className="pagination-info">
                        {t('common.page')} {pagination.currentPage} / {pagination.totalPages}
                    </span>
                    <button
                        className="btn btn-secondary"
                        disabled={!pagination.hasNextPage}
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                    >
                        {t('common.next')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ClimateRiskView;
